import React, { useCallback, useState, useRef, useMemo } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  Controls,
  Background,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  MarkerType,
  Panel,
  useNodesState,
  useEdgesState,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Alert,
  Fab
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Save as SaveIcon,
  FitScreen as FitIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Import custom node components
import TriggerNode from './nodes/TriggerNode';
import ConditionNode from './nodes/ConditionNode';
import ActionNode from './nodes/ActionNode';
import RouterNode from './nodes/RouterNode';
import LoopNode from './nodes/LoopNode';
import DelayNode from './nodes/DelayNode';
import WorkflowSidebar from './WorkflowSidebar';
import NodeConfigDialog from './NodeConfigDialog';
import WorkflowSettingsPanel from './panels/WorkflowSettingsPanel';

// Define node types
const nodeTypes = {
  trigger: TriggerNode,
  condition: ConditionNode,
  action: ActionNode,
  router: RouterNode,
  loop: LoopNode,
  delay: DelayNode
};

// Edge styles
const defaultEdgeOptions = {
  style: { strokeWidth: 2, stroke: '#b1b1b7' },
  type: 'smoothstep',
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
    color: '#b1b1b7'
  },
  animated: false
};

// Inner component that uses React Flow hooks
const WorkflowBuilderInner = ({ workflow, onChange, onSave, onTest, readonly = false }) => {
  const reactFlowWrapper = useRef(null);
  const { fitView, project } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState(workflow?.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(workflow?.edges || []);
  const [selectedNode, setSelectedNode] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  
  // History for undo/redo
  const [history, setHistory] = useState([{ nodes: [], edges: [] }]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Use a ref to track the workflow ID to detect when we're loading a different workflow
  const workflowIdRef = useRef(workflow?._id);
  
  // Initialize nodes and edges from workflow
  React.useEffect(() => {
    // Update when workflow changes (different ID or structure changes)
    const isDifferentWorkflow = workflow?._id !== workflowIdRef.current;
    
    if (isDifferentWorkflow && workflow?.nodes && workflow?.edges) {
      setNodes(workflow.nodes);
      // Transform backend edge types to React Flow visual types
      const visualEdges = workflow.edges.map(edge => ({
        ...edge,
        // If edge doesn't have a React Flow type, add the visual style
        type: edge.type === 'default' || edge.type === 'conditional' || edge.type === 'error' || edge.type === 'loop' 
          ? 'smoothstep'  // Use smoothstep for visual rendering
          : edge.type || 'smoothstep',
        // Preserve the logical type in data for backend
        data: {
          ...edge.data,
          logicalType: edge.type // Store the backend type
        }
      }));
      setEdges(visualEdges);
      workflowIdRef.current = workflow?._id;
    } else if (!workflow?._id && workflow?.nodes && workflow?.edges) {
      // Handle new workflow case (no ID yet)
      setNodes(workflow.nodes);
      const visualEdges = workflow.edges.map(edge => ({
        ...edge,
        type: edge.type === 'default' || edge.type === 'conditional' || edge.type === 'error' || edge.type === 'loop' 
          ? 'smoothstep'
          : edge.type || 'smoothstep',
        data: {
          ...edge.data,
          logicalType: edge.type
        }
      }));
      setEdges(visualEdges);
    }
  }, [workflow?._id]);

  // Save to history
  const saveToHistory = useCallback(() => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ nodes: [...nodes], edges: [...edges] });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [nodes, edges, history, historyIndex]);

  // Handle connection
  const onConnect = useCallback((params) => {
    if (readonly) return;
    
    const newEdge = {
      ...params,
      ...defaultEdgeOptions,
      id: `e${params.source}-${params.target}`,
      animated: true
    };
    
    setEdges((eds) => addEdge(newEdge, eds));
    saveToHistory();
    
    // Notify parent of changes
    onChange && onChange({ nodes, edges: [...edges, newEdge] });
  }, [readonly, setEdges, saveToHistory, onChange, nodes, edges]);

  // Handle node drag stop
  const onNodeDragStop = useCallback((event, node) => {
    if (readonly) return;
    saveToHistory();
    onChange && onChange({ nodes, edges });
  }, [readonly, saveToHistory, onChange, nodes, edges]);

  // Handle node click
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    if (!readonly && node.type !== 'trigger') {
      setConfigDialogOpen(true);
    }
  }, [readonly]);

  // Handle node deletion
  const deleteNode = useCallback((nodeId) => {
    if (readonly) return;
    
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    saveToHistory();
    
    onChange && onChange({
      nodes: nodes.filter((n) => n.id !== nodeId),
      edges: edges.filter((e) => e.source !== nodeId && e.target !== nodeId)
    });
  }, [readonly, setNodes, setEdges, saveToHistory, onChange, nodes, edges]);

  // Handle node duplication
  const duplicateNode = useCallback((node) => {
    if (readonly) return;
    
    const newNode = {
      ...node,
      id: `${node.type}_${Date.now()}`,
      position: {
        x: node.position.x + 100,
        y: node.position.y + 100
      }
    };
    
    setNodes((nds) => [...nds, newNode]);
    saveToHistory();
    
    onChange && onChange({ nodes: [...nodes, newNode], edges });
  }, [readonly, setNodes, saveToHistory, onChange, nodes, edges]);

  // Handle edge deletion
  const onEdgesDelete = useCallback((deletedEdges) => {
    if (readonly) return;
    saveToHistory();
    onChange && onChange({ nodes, edges });
  }, [readonly, saveToHistory, onChange, nodes, edges]);

  // Handle drop
  const onDrop = useCallback(
    (event) => {
      if (readonly) return;
      
      event.preventDefault();
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');
      const nodeDataString = event.dataTransfer.getData('nodeData');

      if (!type) return;

      // Calculate position using React Flow's project function to convert screen to flow coordinates
      const position = project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top
      });

      // Parse node data if available
      let nodeData = {};
      try {
        if (nodeDataString) {
          nodeData = JSON.parse(nodeDataString);
        }
      } catch (e) {
        console.error('Failed to parse node data:', e);
      }

      const newNode = {
        id: `${type}_${Date.now()}`,
        type,
        position,
        data: { 
          label: nodeData.label || `New ${type}`,
          config: {
            subtype: nodeData.subtype || '',
            ...nodeData
          }
        }
      };

      setNodes((nds) => [...nds, newNode]);
      saveToHistory();
      
      onChange && onChange({ nodes: [...nodes, newNode], edges });
    },
    [readonly, setNodes, saveToHistory, onChange, nodes, edges, project]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const { nodes: histNodes, edges: histEdges } = history[newIndex];
      setNodes(histNodes);
      setEdges(histEdges);
      setHistoryIndex(newIndex);
      onChange && onChange({ nodes: histNodes, edges: histEdges });
    }
  }, [historyIndex, history, setNodes, setEdges, onChange]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const { nodes: histNodes, edges: histEdges } = history[newIndex];
      setNodes(histNodes);
      setEdges(histEdges);
      setHistoryIndex(newIndex);
      onChange && onChange({ nodes: histNodes, edges: histEdges });
    }
  }, [historyIndex, history, setNodes, setEdges, onChange]);

  // Handle right click context menu
  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    setSelectedNode(node);
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY
    });
  }, []);

  // Close context menu
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Handle node configuration save
  const handleNodeConfigSave = (config) => {
    if (!selectedNode) return;
    
    const updatedNodes = nodes.map((node) => {
      if (node.id === selectedNode.id) {
        return {
          ...node,
          data: {
            ...node.data,
            label: config.label || node.data.label,
            config: {
              ...config
            }
          }
        };
      }
      return node;
    });
    
    setNodes(updatedNodes);
    saveToHistory();
    setConfigDialogOpen(false);
    onChange && onChange({ nodes: updatedNodes, edges });
  };

  // Validate workflow
  const validateWorkflow = () => {
    const errors = [];
    
    // Check for trigger node
    const triggerNodes = nodes.filter(n => n.type === 'trigger');
    if (triggerNodes.length === 0) {
      errors.push('Workflow must have at least one trigger');
    }
    
    // Check for disconnected nodes
    const connectedNodeIds = new Set();
    edges.forEach(edge => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });
    
    nodes.forEach(node => {
      if (node.type !== 'trigger' && !connectedNodeIds.has(node.id)) {
        errors.push(`Node "${node.data.label}" is not connected`);
      }
    });
    
    return errors;
  };

  // Handle save
  const handleSave = () => {
    const errors = validateWorkflow();
    if (errors.length > 0) {
      alert('Workflow validation errors:\n' + errors.join('\n'));
      return;
    }
    
    // Transform for backend - keep React Flow structure but prepare workflow data
    const workflowData = {
      nodes: nodes.map(node => ({
        id: node.id,
        type: node.type,
        data: {
          ...node.data,
          // Remove UI-specific properties
          isConnectable: undefined,
          isDragging: undefined
        },
        position: node.position
      })),
      edges: edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        type: edge.data?.logicalType || 'default', // Backend edge type
        data: edge.data ? {
          ...edge.data,
          logicalType: undefined // Remove this from data
        } : undefined
      }))
    };
    
    // Extract trigger configuration from trigger node
    const triggerNode = nodes.find(n => n.type === 'trigger');
    if (triggerNode?.data?.config?.subtype) {
      workflowData.trigger = {
        type: triggerNode.data.config.subtype,
        config: triggerNode.data.config
      };
    }
    
    onSave && onSave(workflowData);
  };

  // Handle test
  const handleTest = () => {
    const errors = validateWorkflow();
    if (errors.length > 0) {
      alert('Cannot test workflow:\n' + errors.join('\n'));
      return;
    }
    
    // Transform for backend - same as save
    const workflowData = {
      nodes: nodes.map(node => ({
        id: node.id,
        type: node.type,
        data: {
          ...node.data,
          isConnectable: undefined,
          isDragging: undefined
        },
        position: node.position
      })),
      edges: edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        type: edge.data?.logicalType || 'default',
        data: edge.data ? {
          ...edge.data,
          logicalType: undefined
        } : undefined
      }))
    };
    
    // Extract trigger configuration
    const triggerNode = nodes.find(n => n.type === 'trigger');
    if (triggerNode?.data?.config?.subtype) {
      workflowData.trigger = {
        type: triggerNode.data.config.subtype,
        config: triggerNode.data.config
      };
    }
    
    onTest && onTest(workflowData);
  };

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <Box sx={{ display: 'flex', height: '100%' }}>
        {/* Sidebar */}
        {!readonly && (
          <WorkflowSidebar />
        )}
        
        {/* Main Canvas */}
        <Box sx={{ flex: 1, position: 'relative' }} ref={reactFlowWrapper}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onNodeContextMenu={onNodeContextMenu}
              onNodeDragStop={onNodeDragStop}
              onEdgesDelete={onEdgesDelete}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodeTypes={nodeTypes}
              defaultEdgeOptions={defaultEdgeOptions}
              fitView
              attributionPosition="bottom-left"
            >
              <Background color="#aaa" gap={16} />
              <MiniMap 
                nodeStrokeColor={(n) => {
                  if (n.type === 'trigger') return '#4caf50';
                  if (n.type === 'condition') return '#ff9800';
                  if (n.type === 'action') return '#2196f3';
                  return '#757575';
                }}
                nodeColor={(n) => {
                  if (n.type === 'trigger') return '#4caf50';
                  if (n.type === 'condition') return '#ff9800';
                  if (n.type === 'action') return '#2196f3';
                  return '#757575';
                }}
                nodeBorderRadius={2}
              />
              <Controls />
              
              {/* Custom Controls Panel */}
              <Panel position="top-right">
                <Paper sx={{ p: 1 }}>
                  <Stack direction="row" spacing={1}>
                    {!readonly && (
                      <>
                        <Tooltip title="Undo">
                          <IconButton size="small" onClick={handleUndo} disabled={historyIndex === 0}>
                            <UndoIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Redo">
                          <IconButton size="small" onClick={handleRedo} disabled={historyIndex === history.length - 1}>
                            <RedoIcon />
                          </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem />
                      </>
                    )}
                    <Tooltip title="Fit View">
                      <IconButton size="small" onClick={() => fitView()}>
                        <FitIcon />
                      </IconButton>
                    </Tooltip>
                    <Divider orientation="vertical" flexItem />
                    <Tooltip title="Workflow Settings">
                      <IconButton size="small" onClick={() => setSettingsDialogOpen(true)}>
                        <SettingsIcon />
                      </IconButton>
                    </Tooltip>
                    <Divider orientation="vertical" flexItem />
                    {!readonly && (
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<SaveIcon />}
                          onClick={handleSave}
                        >
                          Save
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PlayIcon />}
                          onClick={handleTest}
                        >
                          Test
                        </Button>
                      </>
                    )}
                  </Stack>
                </Paper>
              </Panel>
            </ReactFlow>
          </Box>
        </Box>
        
        {/* Node Configuration Dialog */}
        <NodeConfigDialog
          open={configDialogOpen}
          node={selectedNode}
          onClose={() => setConfigDialogOpen(false)}
          onSave={handleNodeConfigSave}
        />
        
        {/* Context Menu */}
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          {!readonly && (
            <>
              <MenuItem onClick={() => {
                selectedNode && setConfigDialogOpen(true);
                handleCloseContextMenu();
              }}>
                <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                Configure
              </MenuItem>
              <MenuItem onClick={() => {
                selectedNode && duplicateNode(selectedNode);
                handleCloseContextMenu();
              }}>
                <DuplicateIcon fontSize="small" sx={{ mr: 1 }} />
                Duplicate
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => {
                selectedNode && deleteNode(selectedNode.id);
                handleCloseContextMenu();
              }} sx={{ color: 'error.main' }}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            </>
          )}
        </Menu>
        
        {/* Workflow Settings Dialog */}
        <Dialog
          open={settingsDialogOpen}
          onClose={() => setSettingsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Workflow Settings</Typography>
              <IconButton onClick={() => setSettingsDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <WorkflowSettingsPanel
              workflow={workflow}
              onUpdate={(updatedWorkflow) => {
                if (onSave) {
                  onSave(updatedWorkflow);
                }
                setSettingsDialogOpen(false);
              }}
              onClose={() => setSettingsDialogOpen(false)}
              readOnly={readonly}
            />
          </DialogContent>
        </Dialog>
    </Box>
  );
};

// Wrapper component that provides ReactFlowProvider
const VisualWorkflowBuilder = (props) => {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderInner {...props} />
    </ReactFlowProvider>
  );
};

export default VisualWorkflowBuilder;